/**
 * EL ESTADO DE TRASPASO DE UN CHAT — un solo lugar, tres consumidores.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * ÉSTE SÍ SE PERSISTE, Y NO ES UNA INCOHERENCIA CON LA AGENDA
 * ───────────────────────────────────────────────────────────────────────────────
 * Los tres marcadores del inbox guardan su hecho donde ese hecho vive:
 *
 *   · venta    → denormalizada al chat, porque el pedido está en Shopify, FUERA
 *   · agenda   → calculada de `{tenant}/bookings`, que es nuestra
 *   · traspaso → **en el chat, porque no tiene otra casa**
 *
 * No existe una colección de traspasos de la que derivarlo, y no se inventa una para
 * guardar un objeto de cuatro campos por conversación. La regla no es «persistir» ni
 * «calcular»: es que el marcador lea el sitio donde el hecho ya está.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * EL HECHO ES LA LLAMADA A LA TOOL, NUNCA LA PROSA
 * ───────────────────────────────────────────────────────────────────────────────
 * Hasta el 23-sep-2026 `handoff_required_on` sólo llegaba al prompt como texto y el bot
 * «derivaba» escribiendo una frase. Marcar por coincidencia de frases habría sido
 * construir sobre una afirmación que nadie verificó — la familia exacta de
 * `afirmacionDeAccion`. Medido: 3 promesas de contacto humano en 90 días, las tres en un
 * solo chat y NINGUNA seguida por un humano.
 *
 * Por eso `origen` se compara contra `'bot'` y el campo lo escribe `solicitarTraspaso`.
 * Si algún día un operador marca un traspaso a mano, ese valor será otro y los dos
 * seguirán siendo distinguibles.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * «ATENDIDO» NO SE BORRA — PERO SÓLO ES CIERTO EN EL DETALLE (24-sep-2026)
 * ───────────────────────────────────────────────────────────────────────────────
 * ⚠️ ESTE PÁRRAFO DECÍA OTRA COSA HASTA HOY, Y LA DECISIÓN QUE REVIERTE ERA RAZONABLE.
 * El 23-sep se escribió aquí, sin matices:
 *
 *     «Un traspaso resuelto conserva su marca. El operador tiene que poder ver que a
 *      esta conversación se le prometió una persona y cuánto tardó en llegar — igual
 *      que la cita pasada de la agenda sigue visible.»
 *
 * **Un día de uso la contradijo.** Rodrigo abrió la bandeja al día siguiente y la
 * píldora «Traspaso atendido» seguía ahí, sobre una conversación resuelta hacía 14 h y
 * que ya había seguido su curso. No informaba de nada: ocupaba sitio en la superficie
 * donde se decide a quién atender.
 *
 * El argumento de arriba no era falso — **era cierto sobre el sitio equivocado**:
 *
 *   · la BANDEJA es una superficie de triaje: «¿qué pide algo de mí ahora?»
 *   · el DETALLE es el registro de esa conversación
 *
 * Así que el hecho se queda **para siempre en el detalle**, con su demora, que es donde
 * la frase del 23-sep es verdad y donde hay espacio para ella. Lo que caduca es su
 * presencia en la lista.
 *
 * **EL PENDIENTE NO CADUCA NUNCA.** Mientras nadie responda, esa conversación tiene que
 * seguir estando: es la única de las dos que pide algo.
 *
 * ⚠️ LAS 12 h SON UNA HIPÓTESIS, NO UNA MEDICIÓN. Medido el 24-sep-2026: hay **UN**
 * traspaso en toda la base de datos —el del caso, atendido en 2,9 h—. Con n=1 ningún
 * umbral sale de los datos, y conviene decirlo antes que el número.
 *
 * El argumento es el turno de trabajo: el acuse le sirve a quien estaba cuando ocurrió.
 * Lo atendido esta mañana sigue visible esta tarde; lo de ayer, no.
 *
 * Y las dos alternativas, descartadas con motivo:
 *
 *   48 h, por simetría con `seguimientoVenta`   NO arreglaría el caso: el de Rodrigo
 *     llevaba 14,1 h y ya sobraba. Y la simetría es falsa — una venta vencida sigue en
 *     gris porque es un hecho SOBRE EL CLIENTE, con valor duradero; un traspaso
 *     atendido es un hecho sobre NUESTRO PROCESO, y cerrado es historia operativa.
 *
 *   «sólo si fue hoy», sin constante que defender   Necesita zona horaria, y
 *     `config.timeZone` está guardado en 1 de 7 bots —dos se muestran como Santiago y
 *     se comportan como Nueva York—. Sería importar ese defecto a un sitio nuevo.
 *
 *   una señal en vez de un reloj («ocúltalo cuando la conversación siga»)   Atender un
 *     traspaso ES responder, así que `actualizadoEn` supera a `atendidoEn` a los pocos
 *     segundos. La señal se cumple siempre y no distingue nada.
 *
 * REVISARLO en cuanto haya traspasos de más de un operador. Si con diez la mediana de
 * «cuándo deja de importar» es otra, se cambia — el 12 es lo que no está medido.
 */

/** 12 horas: un turno de trabajo. Ver la cabecera — es hipótesis, no medición. */
export const VENTANA_ATENDIDO_MS = 12 * 60 * 60 * 1000;

/**
 * Cuándo se pidió el traspaso, tolerante a Timestamp, Date, ISO o número.
 * @returns {number} milisegundos, o NaN
 */
export function instanteDeTraspaso(valor) {
  if (!valor) return NaN;
  if (typeof valor.toDate === 'function') return valor.toDate().getTime();
  if (valor instanceof Date) return valor.getTime();
  if (typeof valor === 'number') return valor;
  const p = Date.parse(valor);
  return Number.isFinite(p) ? p : NaN;
}

/**
 * El estado de traspaso de un chat.
 *
 * @param {object} chat - el documento del chat
 * @returns {'ninguno'|'pendiente'|'atendido'}
 *
 * ⚠️ EXIGE `solicitadoEn` CON FECHA LEGIBLE. Un `traspaso: {}` —que ningún escritor
 * produce hoy, pero que un `merge` mal hecho podría dejar— no es un traspaso: marcarlo
 * como pendiente pondría una conversación en la alerta para siempre, sin forma de
 * resolverla, porque no hay instante contra el que comparar la respuesta del humano.
 */
export function estadoSeguimientoTraspaso(chat) {
  const t = chat?.traspaso;
  if (!t) return 'ninguno';
  if (!Number.isFinite(instanteDeTraspaso(t.solicitadoEn))) return 'ninguno';
  return Number.isFinite(instanteDeTraspaso(t.atendidoEn)) ? 'atendido' : 'pendiente';
}

/** Cuánto hace que se atendió, en milisegundos. NaN si no está atendido. */
export function desdeAtendido(chat, ahora = Date.now()) {
  if (estadoSeguimientoTraspaso(chat) !== 'atendido') return NaN;
  return ahora - instanteDeTraspaso(chat.traspaso.atendidoEn);
}

/**
 * ¿SALE EN LA BANDEJA?
 *
 * Es la única de las dos superficies que caduca. El detalle pregunta por el estado
 * directamente y conserva el hecho para siempre — ver la cabecera.
 *
 *   · pendiente → SIEMPRE. Mientras nadie responda, esa conversación pide algo.
 *   · atendido  → sólo dentro de `VENTANA_ATENDIDO_MS`.
 *
 * `desdeAtendido` es finito por construcción cuando el estado es `atendido`:
 * `estadoSeguimientoTraspaso` ya exige que `atendidoEn` sea legible para devolverlo.
 */
export function traspasoVisibleEnLista(chat, ahora = Date.now()) {
  const estado = estadoSeguimientoTraspaso(chat);
  if (estado === 'ninguno') return false;
  if (estado === 'pendiente') return true;
  return desdeAtendido(chat, ahora) <= VENTANA_ATENDIDO_MS;
}

/** Cuánto lleva esperando, en milisegundos. NaN si no está pendiente. */
export function esperaDeTraspaso(chat, ahora = Date.now()) {
  if (estadoSeguimientoTraspaso(chat) !== 'pendiente') return NaN;
  const desde = instanteDeTraspaso(chat.traspaso.solicitadoEn);
  return Number.isFinite(desde) ? ahora - desde : NaN;
}

/** Cuánto tardó en atenderse. NaN si no está atendido. */
export function demoraDeTraspaso(chat) {
  if (estadoSeguimientoTraspaso(chat) !== 'atendido') return NaN;
  const desde = instanteDeTraspaso(chat.traspaso.solicitadoEn);
  const hasta = instanteDeTraspaso(chat.traspaso.atendidoEn);
  return Number.isFinite(desde) && Number.isFinite(hasta) ? hasta - desde : NaN;
}

/** El motivo declarado por el bot, o null. */
export function motivoDeTraspaso(chat) {
  const m = chat?.traspaso?.motivo;
  return typeof m === 'string' && m.trim() !== '' ? m.trim() : null;
}

/**
 * ¿Se le prometió al cliente un plazo, y cuál?
 *
 * Devuelve null cuando la frase salió SIN plazo — que es el caso de los 5 de 7 bots sin
 * `schedulingConfig`. El operador necesita distinguirlo: si no se prometió hora, no está
 * llegando tarde a nada.
 */
export function plazoPrometido(chat) {
  const tramo = chat?.traspaso?.tramo;
  if (!tramo || tramo.sinPlazo || !tramo.inicio || !tramo.fin) return null;
  return { dia: tramo.dia || null, inicio: tramo.inicio, fin: tramo.fin, enCurso: Boolean(tramo.enCurso) };
}
